import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const SERVICE_LINKS = [
  { path: '/perimenopause', label: 'Perimenopause' },
  { path: '/menopause', label: 'Menopause' },
  { path: '/pmos', label: 'PMOS (formerly PCOS)' },
  { path: '/pmdd', label: 'PMDD' },
  { path: '/postpartum', label: 'Postpartum' },
  { path: '/thyroid', label: 'Thyroid' },
];

/**
 * Compact Services dropdown intended to drop into existing inline headers
 * on standalone pages (PMOS, Menopause, PMDD, Postpartum, Thyroid,
 * Perimenopause, BridgePage). Works on both desktop (hover) and mobile (tap).
 *
 * Props:
 *  - testIdPrefix: string. Used to scope data-testids per host page.
 *  - alignRight:  bool. Aligns the dropdown to the right edge of the trigger.
 */
const ServicesDropdown = ({ testIdPrefix = 'services-dd', alignRight = true }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleEnter = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div
      className={`minimal-services-dropdown ${alignRight ? 'minimal-services-dropdown-right' : ''}`}
      ref={wrapperRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        className={`pcos-nav-link minimal-services-trigger ${open ? 'open' : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testIdPrefix}-trigger`}
      >
        <span>Services</span>
        <ChevronDown size={14} className={`minimal-services-chev ${open ? 'open' : ''}`} />
      </button>

      {open && (
        <div className="minimal-services-menu" data-testid={`${testIdPrefix}-menu`}>
          <a href="/services" className="minimal-services-all" data-testid={`${testIdPrefix}-all`}>
            View all services
          </a>
          <ul>
            {SERVICE_LINKS.map((s) => (
              <li key={s.path}>
                <a
                  href={s.path}
                  data-testid={`${testIdPrefix}-${s.label.toLowerCase().split(' ')[0]}`}
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ServicesDropdown;
